import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { usePopupMenu } from "../../../../components/primitives/navigation/PopupMenu";
import { InternalPage } from "../../../../components/primitives/layout/InternalPage";
import { PageHeader } from "../../../../components/primitives/layout/PageHeader";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../../../components/primitives/feedback/EmptyState";
import { ConfirmDialog } from "../../../../components/primitives/feedback/ConfirmDialog";
import { FormDialog } from "../../../../components/primitives/feedback/FormDialog";
import { useFormDialog } from "../../../../components/primitives/feedback/FormDialog";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { RoleChip } from "../../../../components/domain/data-display/RoleChip";
import { useConfirmHandler } from "../../../../hooks/useConfirmHandler";
import { useAdminUsers } from "./useAdminUsers";
import type { AdminUser, CreateAdminUserParams } from "../../../../lib/adminApi";
import { type AppRole, APP_ROLES } from "@shared/types/roles.ts";
import { useTaxonomy } from "../../../../context/TaxonomyProvider";

// ── Create user dialog ─────────────────────────────────────────────────────────

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (params: CreateAdminUserParams) => Promise<void>;
}

function CreateUserDialog({ open, onClose, onCreate }: CreateUserDialogProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [sendInvite, setSendInvite] = useState(false);
  const [credentials, setCredentials] = useState("");
  const [bio, setBio] = useState("");

  function reset() {
    setEmail("");
    setFullName("");
    setRoles([]);
    setSendInvite(false);
    setCredentials("");
    setBio("");
  }

  const isExpert = roles.includes("expert_insights.expert");

  const { dialogProps } = useFormDialog({
    onSubmit: () =>
      onCreate({
        email: email.trim(),
        full_name: fullName.trim(),
        roles,
        send_invite: sendInvite,
        ...(isExpert && { credentials: credentials.trim(), bio: bio.trim() }),
      }),
    onClose: () => {
      reset();
      onClose();
    },
  });

  function toggleRole(role: AppRole) {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  const valid =
    email.trim().length > 0 &&
    fullName.trim().length > 0 &&
    roles.length > 0 &&
    (!isExpert || (credentials.trim().length > 0 && bio.trim().length > 0));

  return (
    <FormDialog
      open={open}
      title="New user"
      submitLabel="Create"
      submitDisabled={!valid}
      maxWidth="xs"
      {...dialogProps}
    >
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        size="small"
        fullWidth
        disabled={dialogProps.loading}
      />
      <TextField
        label="Full name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        size="small"
        fullWidth
        disabled={dialogProps.loading}
      />
      <Stack>
        <Typography variant="caption" color="text.secondary">
          Roles
        </Typography>
        {APP_ROLES.map((role) => (
          <FormControlLabel
            key={role}
            label={t(`roles.${role.replace(".", "_")}`, { defaultValue: role })}
            control={
              <Checkbox
                size="small"
                checked={roles.includes(role)}
                onChange={() => toggleRole(role)}
                disabled={dialogProps.loading}
              />
            }
          />
        ))}
      </Stack>
      {isExpert && (
        <>
          <TextField
            label="Credentials"
            value={credentials}
            onChange={(e) => setCredentials(e.target.value)}
            size="small"
            fullWidth
            required
            disabled={dialogProps.loading}
            placeholder="PhD, Nutrition Science, King's College London"
            helperText="Shown alongside their insights"
          />
          <TextField
            label="Bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={3}
            disabled={dialogProps.loading}
            helperText="A short introduction about themselves"
          />
        </>
      )}
      <FormControlLabel
        label="Send invite email"
        control={
          <Checkbox
            size="small"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
            disabled={dialogProps.loading}
          />
        }
      />
    </FormDialog>
  );
}

// ── Edit roles dialog ──────────────────────────────────────────────────────────

interface EditRolesDialogProps {
  user: AdminUser;
  onClose: () => void;
  onSave: (userId: string, roles: AppRole[]) => Promise<void>;
}

function EditRolesDialog({ user, onClose, onSave }: EditRolesDialogProps) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<AppRole[]>(user.roles);

  const { dialogProps } = useFormDialog({
    onSubmit: () => onSave(user.id, roles),
    onClose,
  });

  function toggleRole(role: AppRole) {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  return (
    <FormDialog
      open
      title={`Edit roles — ${user.email}`}
      submitLabel="Save"
      maxWidth="xs"
      {...dialogProps}
    >
      {APP_ROLES.map((role) => (
        <FormControlLabel
          key={role}
          label={t(`roles.${role.replace(".", "_")}`, { defaultValue: role })}
          control={
            <Checkbox
              size="small"
              checked={roles.includes(role)}
              onChange={() => toggleRole(role)}
              disabled={dialogProps.loading}
            />
          }
        />
      ))}
    </FormDialog>
  );
}

// ── Manage expert fields dialog ────────────────────────────────────────────────

interface ManageExpertFieldsDialogProps {
  user: AdminUser;
  onClose: () => void;
  onSave: (userId: string, fields: string[]) => Promise<void>;
}

function ManageExpertFieldsDialog({ user, onClose, onSave }: ManageExpertFieldsDialogProps) {
  const taxonomy = useTaxonomy();
  const [fields, setFields] = useState<string[]>(user.expertFields ?? []);

  const { dialogProps } = useFormDialog({
    onSubmit: () => onSave(user.id, fields),
    onClose,
  });

  function toggleField(urn: string) {
    setFields((prev) => (prev.includes(urn) ? prev.filter((f) => f !== urn) : [...prev, urn]));
  }

  function toggleDomain(domainUrn: string) {
    const domainFieldUrns: string[] = (taxonomy.find(domainUrn)?.children ?? []).map((n) => n.urn);
    const allSelected = domainFieldUrns.every((urn) => fields.includes(urn));
    if (allSelected) {
      setFields((prev) => prev.filter((f) => !domainFieldUrns.includes(f)));
    } else {
      setFields((prev) => [...prev, ...domainFieldUrns.filter((urn) => !prev.includes(urn))]);
    }
  }

  const domains = taxonomy.domains;

  return (
    <FormDialog
      open
      title={`Expert fields — ${user.email}`}
      submitLabel="Save"
      maxWidth="xs"
      {...dialogProps}
    >
      {domains.map((domain) => {
        const domainFields = domain.children;
        const checkedCount = domainFields.filter((n) => fields.includes(n.urn)).length;
        const allChecked = checkedCount === domainFields.length;
        const someChecked = checkedCount > 0;
        return (
          <Stack key={domain.urn}>
            <FormControlLabel
              label={domain.enName}
              slotProps={{ typography: { variant: "body2", fontWeight: 600 } }}
              control={
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={!allChecked && someChecked}
                  onChange={() => toggleDomain(domain.urn)}
                  disabled={dialogProps.loading}
                />
              }
            />
            <FormGroup sx={{ pl: 3.5 }}>
              {domainFields.map((field) => (
                <FormControlLabel
                  key={field.urn}
                  label={field.enName}
                  control={
                    <Checkbox
                      size="small"
                      checked={fields.includes(field.urn)}
                      onChange={() => toggleField(field.urn)}
                      disabled={dialogProps.loading}
                    />
                  }
                />
              ))}
            </FormGroup>
          </Stack>
        );
      })}
    </FormDialog>
  );
}

// ── Per-row actions dropdown ───────────────────────────────────────────────────

interface UserActionsMenuProps {
  user: AdminUser;
  onEditRoles: () => void;
  onCreateExpertProfile: () => void;
  onManageExpertFields: () => void;
  onToggleActive: () => void;
  onSendPasswordReset: () => void;
}

function UserActionsMenu({
  user,
  onEditRoles,
  onCreateExpertProfile,
  onManageExpertFields,
  onToggleActive,
  onSendPasswordReset,
}: UserActionsMenuProps) {
  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [
      { label: "Edit roles", onClick: onEditRoles },
      ...(!user.hasExpertProfile
        ? [{ label: "Create expert profile", onClick: onCreateExpertProfile }]
        : [{ label: "Manage expert fields", onClick: onManageExpertFields }]),
      { label: "Send password reset", onClick: onSendPasswordReset },
    ],
    [
      {
        label: user.isBanned ? "Reactivate" : "Deactivate",
        onClick: onToggleActive,
        color: user.isBanned ? "primary" : "warning",
      } as const,
    ],
  ]);

  return (
    <>
      <IconButton size="small" onClick={setPopupMenuAnchor} aria-label="User actions">
        <MoreVertIcon />
      </IconButton>
      {menu}
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

/** Admin-only page: list all users, create new users, edit roles, manage expert fields, deactivate/reactivate. */
export default function AdminUsers() {
  const {
    users,
    loading,
    error,
    handleCreate,
    handleUpdateRoles,
    handleDeactivate,
    handleReactivate,
    handleCreateExpertProfile,
    handleUpdateExpertFields,
    handleSendPasswordReset,
  } = useAdminUsers();

  const { enqueueSnackbar } = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [managingFieldsUser, setManagingFieldsUser] = useState<AdminUser | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const deactivateConfirm = useConfirmHandler({
    title: "Deactivate user",
    message: "This will block the user from signing in. You can reactivate them at any time.",
    confirmLabel: "Deactivate",
    confirmColor: "warning",
  });

  function toggleActive(user: AdminUser) {
    setActionError(null);
    if (user.isBanned) {
      void handleReactivate(user.id).catch((e: unknown) =>
        setActionError(e instanceof Error ? e.message : "Action failed")
      );
    } else {
      deactivateConfirm.handle(() => handleDeactivate(user.id));
    }
  }

  const columns: DataSequenceColumn<AdminUser>[] = [
    {
      key: "name",
      header: "Name",
      cardRole: "primary",
      cell: (u) =>
        u.fullName ?? (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      key: "email",
      header: "Email",
      cell: (u) => u.email,
    },
    {
      key: "roles",
      header: "Roles",
      cell: (u) => (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {u.roles.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              None
            </Typography>
          ) : (
            u.roles.map((r) => <RoleChip key={r} role={r} />)
          )}
        </Stack>
      ),
    },
    {
      key: "expert_profile",
      header: "Expert profile",
      cardRole: "hidden",
      cell: (u) =>
        u.hasExpertProfile ? (
          <Tooltip title="Has expert insights profile">
            <CheckCircleOutlineIcon color="success" />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (u) => (
        <Chip
          label={u.isBanned ? "Deactivated" : "Active"}
          color={u.isBanned ? "default" : "success"}
          size="small"
        />
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cardRole: "action",
      cell: (u) => (
        <UserActionsMenu
          user={u}
          onEditRoles={() => setEditingUser(u)}
          onCreateExpertProfile={() =>
            void handleCreateExpertProfile(u.id).catch((e: unknown) =>
              setActionError(e instanceof Error ? e.message : "Action failed")
            )
          }
          onManageExpertFields={() => setManagingFieldsUser(u)}
          onToggleActive={() => void toggleActive(u)}
          onSendPasswordReset={() =>
            void handleSendPasswordReset(u.id)
              .then(() =>
                enqueueSnackbar(`Password reset email sent to ${u.email}`, { variant: "success" })
              )
              .catch((e: unknown) =>
                setActionError(e instanceof Error ? e.message : "Action failed")
              )
          }
        />
      ),
    },
  ];

  return (
    <InternalPage width="full">
      <PageHeader
        title="Users"
        subtitle="Everyone with an account: change their roles, manage their expert fields, reset their password or deactivate them."
      />

      {actionError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {actionError}
        </Typography>
      )}

      {loading ? (
        <LoadingState label="Loading users…" />
      ) : error ? (
        <EmptyState title="Failed to load" message={error} />
      ) : (
        <DataSequence
          columns={columns}
          rows={users}
          getRowId={(u) => u.id}
          emptyMessage="No users yet."
        />
      )}

      <Fab
        color="primary"
        size="medium"
        onClick={() => setCreateOpen(true)}
        sx={{ position: "fixed", bottom: 24, right: 24 }}
        aria-label="Create user"
      >
        <AddIcon />
      </Fab>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />
      {editingUser && (
        <EditRolesDialog
          key={editingUser.id}
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleUpdateRoles}
        />
      )}
      {managingFieldsUser && (
        <ManageExpertFieldsDialog
          key={managingFieldsUser.id}
          user={managingFieldsUser}
          onClose={() => setManagingFieldsUser(null)}
          onSave={handleUpdateExpertFields}
        />
      )}
      <ConfirmDialog {...deactivateConfirm.dialogProps} />
    </InternalPage>
  );
}
